import React, { lazy, Suspense } from "react";
import { Loader } from "@sicredi/react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { queryClient } from "./query-client-config";
import { PAGE } from "#/constants";
import { OidcProvider, withOidcSecure } from "@axa-fr/react-oidc";
import { configParceriasGovBR } from "./api/oidc/oidcConfig";
import AuthenticatingPage from "./components/Auth/Authenticating/Authenticating";
import AuthenticatingErrorPage from "./components/Auth/AuthenticatingError/AuthenticatingError";
import CallbackSuccessPage from "./components/Auth/CallbackSuccess/CallbackSuccess";
import LoadingPage from "./components/Auth/Loading/Loading";
import SessionLostPage from "./components/Auth/SessionLost/SessionLost";
import HeaderContainer from "./components/Header/HeaderContainer";
import FooterContainer from "./components/Footer/FooterContainer";
import { PointsProvider } from "./contexts/PointsContext/PointsContext";
import { MilesProvider } from "./contexts/milesPropertiesContext";

const LazyHome = lazy(() => import("#/containers/Home"));
const LazySoon = lazy(() => import("#/containers/Soon"));
const LazyRedirectUser = lazy(() => import("#/containers/RedirectUser"));
const LazyMilesRedemption = lazy(() => import("#/containers/MilesRedemption"));
const LazyLiveloRedemption = lazy(() => import("#/containers/LiveloRedemption"));
const LazyLiveloSuccess = lazy(() => import("#/containers/LiveloSuccess"));
const LazyNotFound = lazy(() => import("#/containers/NotFound"));

export const Loading = () => <LoadingPage />;
export const AuthenticatingError = () => <AuthenticatingErrorPage />;
export const Authenticating = () => <AuthenticatingPage />;
export const SessionLost = () => <SessionLostPage />;
export const ServiceWorkerNotSupported = () => <ServiceWorkerNotSupported />;
export const CallBackSuccess = () => <CallbackSuccessPage />;

const RouteLayout = () => {
  const location = useLocation();
  const showHeaderAndFooter =
    location.pathname !== PAGE.ROOT() && location.pathname !== PAGE.SOON();

  return (
    <>
      {showHeaderAndFooter && <HeaderContainer />}
      <Switch>
        <Route
          exact
          path={PAGE.ROOT()}
          component={withOidcSecure(LazyRedirectUser)}
        />
        <Route exact path={PAGE.SOON()} component={withOidcSecure(LazySoon)} />
        <Route exact path={PAGE.HOME()} component={withOidcSecure(LazyHome)} />

        <Route
          exact
          path={PAGE.MILESREDEMPTION()}
          component={withOidcSecure(LazyMilesRedemption)}
        />

        <Route
          exact
          path={PAGE.LIVELOREDEMPTION()}
          component={withOidcSecure(LazyLiveloRedemption)}
        />

        <Route exact path={PAGE.LIVELOSUCCESS()} component={withOidcSecure(LazyLiveloSuccess)} />


        <Route path="*" component={LazyNotFound} />
      </Switch>
      {showHeaderAndFooter && <FooterContainer />}
    </>
  );
};

export const Routes: React.FC = () => (
  <OidcProvider
    configuration={window.env?.OIDC_AUTH ?? configParceriasGovBR}
    loadingComponent={Loading}
    authenticatingErrorComponent={AuthenticatingError}
    authenticatingComponent={Authenticating}
    sessionLostComponent={SessionLost}
    serviceWorkerNotSupportedComponent={ServiceWorkerNotSupported}
    callbackSuccessComponent={CallBackSuccess}
  >
    <Router basename={process.env.PUBLIC_URL}>
      <Suspense fallback={<Loader show fullScreen />}>
        <QueryClientProvider client={queryClient}>
          <PointsProvider>
            <MilesProvider>
              <RouteLayout />
            </MilesProvider>
          </PointsProvider>
        </QueryClientProvider>
      </Suspense>
    </Router>
  </OidcProvider>
);
